import {
  abTest as smokeAbTest,
  TEST_CONFIG as SMOKE_TEST_CONFIG,
} from 'js/ab_tests/tests/autotest_login_modal_2026_q3_v1_smoke';
import {
  abTest as waveAbTest,
  TEST_CONFIG as WAVE_TEST_CONFIG,
} from 'js/ab_tests/tests/autotest_login_modal_2026_q3_v1';

import type { LoginModalEventDetail } from '../LoginModalManager';
import { LoginModalAutotestHooks, PartialConfigurableText } from '../types';
import { ARMS, Arm, ArmSpec, Bucket, OpenContext } from './armRegistry';
import {
  AutotestStamp,
  fireImpression,
  stampTrackingContext,
} from './tracking';

export const WAVE_TEST_NAME = 'autotest_login_modal_2026_q3_v1';
export const SMOKE_TEST_NAME = 'autotest_login_modal_2026_q3_v1_smoke';

export type LoginModalAutotestResolution = {
  detail: LoginModalEventDetail;
  autotest?: LoginModalAutotestHooks;
};

type ResolvedTest = {
  testName: string;
  active: boolean;
  bucket: Bucket;
};

// Neither yml declares a holdout, so 'holdout' is unreachable; the framework
// return type still includes it, and control is its only safe reading.
function normalizeBucket(bucket: Bucket | 'holdout'): Bucket {
  return bucket === 'holdout' ? 'control' : bucket;
}

// The real key outranks the smoke key; a key drives dispatch when it is
// active OR its abTest() resolved off-control (a ?ab_test[key]=arm override
// or a prior-bucketing cookie). With both keys inactive and no override —
// the as-merged state until activation — this returns null and the harness
// is a pure passthrough. Calling abTest() here is the wave's sole membership
// recording trigger (records only while that key is active; sessionStorage
// dedupes repeats).
function resolveBucket(): ResolvedTest | null {
  const waveActive: boolean = WAVE_TEST_CONFIG.active;
  const waveBucket = normalizeBucket(waveAbTest());
  if (waveActive || waveBucket !== 'control') {
    return { testName: WAVE_TEST_NAME, active: waveActive, bucket: waveBucket };
  }

  const smokeActive: boolean = SMOKE_TEST_CONFIG.active;
  const smokeBucket = normalizeBucket(smokeAbTest());
  if (smokeActive || smokeBucket !== 'control') {
    return {
      testName: SMOKE_TEST_NAME,
      active: smokeActive,
      bucket: smokeBucket,
    };
  }

  return null;
}

export function resolveLoginModalAutotest(
  detail: LoginModalEventDetail,
): LoginModalAutotestResolution {
  try {
    const resolved = resolveBucket();
    if (!resolved) {
      return { detail };
    }

    const stamp: AutotestStamp = {
      ...(resolved.active && { autotest: resolved.testName }),
      autotest_bucket: resolved.bucket,
      modal_version:
        detail.headerVariant === 'yourPaperCard' ? 'paper_card' : 'default',
    };

    // Per-arm denominator, control included (the documented no-impression gap).
    fireImpression(detail.clickTrackingContext, stamp);

    if (resolved.bucket === 'control') {
      // Control MUST see the original detail object, untouched — the codified
      // Q2 v1 lesson. Enforced by the identity test in harness.test.ts.
      return { detail };
    }

    return armResolution(detail, resolved.bucket, stamp);
  } catch {
    // Any harness failure degrades to exact control behavior.
    return { detail };
  }
}

function armResolution(
  detail: LoginModalEventDetail,
  arm: Arm,
  stamp: AutotestStamp,
): LoginModalAutotestResolution {
  const ctx: OpenContext = { detail };
  const spec = ARMS[arm];
  return {
    detail: {
      ...detail,
      configuredText: mergeConfiguredText(
        detail.configuredText,
        spec?.configuredTextOverride?.(ctx),
      ),
      clickTrackingContext: stampTrackingContext(
        detail.clickTrackingContext,
        stamp,
      ),
    },
    autotest: buildHooks(spec, ctx),
  };
}

function buildHooks(
  spec: ArmSpec | undefined,
  ctx: OpenContext,
): LoginModalAutotestHooks | undefined {
  if (!spec) return undefined;

  const slots = spec.slots?.(ctx);
  const hooks: LoginModalAutotestHooks = {
    ...(slots && { slots }),
    ...(spec.wrapOnSuccess && { wrapOnSuccess: spec.wrapOnSuccess }),
    ...(spec.onClose && { onClose: () => spec.onClose?.(ctx) }),
    ...(spec.ctaDefaultExpanded && {
      ctaDefaultExpanded: spec.ctaDefaultExpanded(ctx),
    }),
  };
  return Object.keys(hooks).length > 0 ? hooks : undefined;
}

// Per-page shallow merge, arm override winning — the same merge shape
// LoginModal applies over its defaults, so empty-string overrides (quiet
// mode) blank slots exactly like production configuredText senders do.
function mergeConfiguredText(
  base: PartialConfigurableText | undefined,
  override: PartialConfigurableText | undefined,
): PartialConfigurableText | undefined {
  if (!override) return base;
  if (!base) return override;
  return {
    buttonsPage: { ...base.buttonsPage, ...override.buttonsPage },
    emailEntryPage: { ...base.emailEntryPage, ...override.emailEntryPage },
    emailRegistrationPage: {
      ...base.emailRegistrationPage,
      ...override.emailRegistrationPage,
    },
    emailLoginPage: { ...base.emailLoginPage, ...override.emailLoginPage },
  };
}

export function useAutotestPageInit(): void {
  // PR-2 wires the page-level module here (session-trail push, dismissal-dock
  // rehydration). The production call site lands with PR-1 so later PRs stay
  // out of production files.
}
